import React, { useState, useMemo, useEffect, useCallback } from 'react';
import './mcp.css';
import axios from 'axios';
import { message, Modal, Select, Input, Tabs, List, Avatar, Tag, Button, Tooltip, Segmented, Alert } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';
import {
  FileOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';

const manifestByKey = {
  collision: 'https://app.geotab.com/addins/geotab/accidentreconstruction/manifest.json',
  'data-connector': 'https://app.geotab.com/addins/geotab/geotab-data-connector-demo/manifest.json',
  'drive-fuel-tracker': 'https://app.geotab.com/addins/geotab/drive-fuel-tracker/manifest.json',
  'eld-info': 'https://app.geotab.com/addins/geotab/eld-support/manifest.json',
  'eld-settings': 'https://app.geotab.com/addins/geotab/eld-settings-validator/manifest.json',
  evsa: 'https://app.geotab.com/addins/geotab/evsa/manifest.json',
  'map-replay': 'https://app.geotab.com/addins/mygeotabcameraaddin/production/manifest.json',
  'camera-addin': 'https://app.geotab.com/addins/mygeotabcameraaddin/production/manifest.json',
  roadside: 'https://app.geotab.com/addins/geotab/roadside-assistance/manifest.json',
};

const presetConfigByKey = {
  tabs: `{
  "name": "Tabs",
  "supportEmail": "justin@fleet-nomics.com",
  "version": "1.0",
  "items": [
    {
      "url": "signInButton.html",
      "path": "SurveillanceCameras",
      "menuName": {
        "en": "Surveillance Cameras"
      },
      "svgIcon": "https://geotab-images.s3.us-east-2.amazonaws.com/icon.svg"
    }
  ],
  "isSigned": false
}`,
  'iox-output': `{
  "name": "IOX Output",
  "supportEmail": "support@geotab.com",
  "version": "1.1.0",
  "items": [
    {
      "url": "https://cdn.jsdelivr.net/gh/Geotab/sdk-addin-samples@master/addin-iox-output/dist/ioxOutput.html",
      "path": "AdministrationLink/",
      "menuName": {
        "en": "IOX Output"
      },
      "icon": "https://cdn.jsdelivr.net/gh/Geotab/sdk-addin-samples@master/addin-iox-output/dist/images/icon.svg"
    }
  ],
  "isSigned": false
}`,
  'last-insert-key-report': `{
  "name": "Last Insert Key Report",
  "supportEmail": "dmitryradyno@geotab.com",
  "version": "1.0",
  "items": [
    {
      "url": "https://www.geotab.com/mygeotab/addin/lastkeyinsert/lastinsertkey.html",
      "path": "ActivityLink/",
      "menuName": {
        "en": "Last Insert Key Report"
      }
    }
  ],
  "isSigned": false
}`,
};

const defaultConfigJson = `{
  "name": "",
  "supportEmail": "ramisj12@gmail.com",
  "version": "1.0",
  "items": [
    {
      "url": "",
      "path": "",
      "menuName": {
        "en": ""
      }
    }
  ]
}`;

const McpAddinsConfiguration = () => {
  const { token } = useAuth();
  const [geotabDatabases, setGeotabDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [allowUnverified, setAllowUnverified] = useState(true);
  const [addins, setAddins] = useState([
    { key: 'collision', name: 'Collision Reconstruction', status: 'verified' },
    { key: 'data-connector', name: 'Data Connector Demo', status: 'verified' },
    { key: 'drive-fuel-tracker', name: 'Drive Fuel Tracker (by Geotab)', status: 'verified' },
    { key: 'eld-info', name: 'ELD Info', status: 'verified' },
    { key: 'eld-settings', name: 'EldSettingsValidator', status: 'verified' },
    { key: 'evsa', name: 'EVSA', status: 'verified' },
    { key: 'iox-output', name: 'IOX Output', status: 'unverified' },
    { key: 'last-insert-key-report', name: 'Last Insert Key Report', status: 'unverified' },
    { key: 'map-replay', name: 'MapReplay', status: 'unverified' },
    { key: 'camera-addin', name: 'mygeotab-camera-addin', status: 'verified' },
    { key: 'roadside', name: 'Roadside Assistance', status: 'verified' },
    { key: 'tabs', name: 'Tabs', status: 'unverified' },
  ]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeModalTab, setActiveModalTab] = useState('config');
  const [configJson, setConfigJson] = useState(defaultConfigJson);

  const authHeader = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  useEffect(() => {
    const fetchGeotabDatabases = async () => {
      try {
        const { data } = await axios.get('/api/integrations/geotab/accounts', { headers: authHeader() });
        const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        setGeotabDatabases(items);
        if (items.length > 0) {
          setSelectedDatabase(items[0].database);
        }
      } catch (error) {
        console.error('Failed to fetch Geotab databases:', error);
      }
    };
    fetchGeotabDatabases();
  }, [authHeader]);

  const handleRegisterAddin = async () => {
    if (!selectedDatabase) {
      message.error('Please select a Geotab database');
      return;
    }

    try {
      setRegistering(true);
      await axios.post('/api/geotab-addins/register', {
        geotabDatabase: selectedDatabase,
        addinConfig: configJson,
      }, { headers: authHeader() });

      message.success('Add-in registered successfully to your Geotab account!');
      setModalOpen(false);
    } catch (error) {
      console.error('Failed to register add-in:', error);
      message.error(error?.response?.data?.error || 'Failed to register add-in');
    } finally {
      setRegistering(false);
    }
  };

  const openAddModal = () => {
    setEditing(null);
    setActiveModalTab('config');
    setConfigJson(defaultConfigJson);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditing(item);
    setActiveModalTab('config');
    const preset = presetConfigByKey[item?.key];
    if (preset) {
      setConfigJson(preset);
    } else {
      const url = manifestByKey[item?.key] || '';
      setConfigJson(url ? `{\n  "url": "${url}"\n}` : `{\n  "url": ""\n}`);
    }
    setModalOpen(true);
  };

  const handleDelete = (key) => {
    setAddins(prev => prev.filter(a => a.key !== key));
  };

  const statusTag = (status) => (
    <Tag color={status === 'verified' ? 'green' : 'orange'} style={{ marginInlineStart: 8 }}>
      {status === 'verified' ? 'Verified' : 'Unverified'}
    </Tag>
  );

  const dataToRender = useMemo(() => addins, [addins]);

  return (
    <section className="mcp-card3d p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-display text-lg font-bold text-ink">Add-Ins Management</h3>
          <p className="mt-1 text-sm text-ink-200">Configure and manage your MyGeotab add-ins.</p>
        </div>
        <button
          type="button"
          onClick={openAddModal}
          className="inline-flex items-center gap-1 rounded-full border border-paper-300 bg-paper-50 px-3 py-1.5 text-sm font-semibold text-ink transition hover:bg-paper-200"
        >
          <PlusOutlined /> Add-In
        </button>
      </div>

      <Alert
        type="warning"
        showIcon
        icon={<ExclamationCircleOutlined />}
        message={<span className="text-sm text-ink">Unverified Add-Ins could have potential security risks. We recommend performing a security audit before allowing them.</span>}
        style={{ marginBottom: 16 }}
      />

      <div className="mb-4 flex items-center gap-4">
        <span className="text-sm font-semibold text-ink">Allow unverified Add-Ins</span>
        <Segmented
          options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
          value={allowUnverified}
          onChange={(v) => setAllowUnverified(Boolean(v))}
        />
      </div>

      <List
        itemLayout="horizontal"
        dataSource={dataToRender}
        renderItem={(item) => (
          <List.Item
            actions={[
              statusTag(item.status),
              <Tooltip key="edit" title="Edit">
                <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(item)} />
              </Tooltip>,
              <Tooltip key="delete" title="Delete">
                <Button danger type="text" icon={<DeleteOutlined />} onClick={() => handleDelete(item.key)} />
              </Tooltip>,
            ]}
            className="rounded-2xl border border-paper-300 bg-paper-50 px-4 py-3 mb-3"
          >
            <List.Item.Meta
              avatar={<Avatar size={32} icon={<FileOutlined />} className="bg-paper-200 text-ink" />}
              title={<span className="font-semibold text-ink">{item.name}</span>}
            />
          </List.Item>
        )}
      />

      <Modal
        title={editing ? `Add-In - ${editing.name}` : 'Add-In'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Tabs
          activeKey={activeModalTab}
          onChange={setActiveModalTab}
          items={[
            {
              key: 'config',
              label: 'Configuration',
              children: (
                <div>
                  <Alert
                    message="Register to Geotab"
                    description="Configure your add-in below and register it to your Geotab account. It will appear in your MyGeotab Add-Ins menu."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <div className="mb-2 text-sm font-semibold text-ink">Select Geotab Database</div>
                  <Select
                    style={{ width: '100%', marginBottom: 16 }}
                    value={selectedDatabase}
                    onChange={setSelectedDatabase}
                    placeholder="Select a Geotab database"
                  >
                    {geotabDatabases.map(db => (
                      <Select.Option key={db.database} value={db.database}>
                        {db.name || db.database}
                      </Select.Option>
                    ))}
                  </Select>

                  <div className="mb-2 text-sm font-semibold text-ink">Configuration JSON</div>
                  <Input.TextArea
                    rows={12}
                    value={configJson}
                    onChange={(e) => setConfigJson(e.target.value)}
                    className="font-mono"
                  />
                  <div className="mt-4 flex justify-end gap-2">
                    <button type="button" className="mcp-btn-text" onClick={() => setModalOpen(false)}>Cancel</button>
                    <button type="button" className="mcp-btn-ghost" onClick={() => setModalOpen(false)}>Save Locally</button>
                    <button
                      type="button"
                      className="mcp-btn-sun"
                      onClick={handleRegisterAddin}
                      disabled={registering || !selectedDatabase}
                    >
                      {registering ? 'Registering…' : 'Register to Geotab'}
                    </button>
                  </div>
                </div>
              ),
            },
            {
              key: 'files',
              label: 'Files',
              children: (
                <div>
                  <div className="rounded-2xl border border-dashed border-paper-300 bg-paper p-6 text-center">
                    <p className="text-ink-200">Drop your ZIP-archived Add-In or Add-In configuration file here, or click to browse.</p>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button type="button" className="mcp-btn-text" onClick={() => setModalOpen(false)}>Cancel</button>
                    <button type="button" className="mcp-btn-sun" onClick={() => setModalOpen(false)}>Done</button>
                  </div>
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </section>
  );
};

export default McpAddinsConfiguration;
